import { useParams } from "react-router-dom";
import {
  useLeadsContainerStore,
  useLeadsStore,
} from "../../../../stores/LeadsStore";
import { useEffect, useState } from "react";
import { useLanguage } from "../../../../stores/LanguageStore";
import { enrichPhonePrm } from "../../../../api/PRMAPI";
import {
  Button,
  Loader,
  Popover,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconChevronRight, IconDeviceMobileSearch, IconUserSearch } from "@tabler/icons-react";
import { getAIKeys } from "../../../../api/ConfigsApi";
import { deselectAll } from "../utils";
import { transformFilterToServer } from "../../../../functions/utilFunctions";

const defaults = ["opener", "clicker", "answerer", "to_call", "out_of_office"];
const enrichType = "dropcontact";

const LeadsEnrichEmail = ({ handleClose, setLoadingJobs, tableFilter, tableSelection, handleFinish }) => {
  const lang = useLanguage((s) => s.language);
  const query = useLeadsStore((s) => s.query);

  const globalFilter = useLeadsStore((s) => s.filter);
  const globalSelectedIds = useLeadsContainerStore((s) => s.selectedIds);
  const unselectedIds = useLeadsContainerStore((s) => s.unselectedIds);
  const selectedColumns = useLeadsContainerStore((s) => s.selectedColumns);

  const selectedIds = tableSelection || globalSelectedIds;
  const filter = tableFilter ? transformFilterToServer(tableFilter) : globalFilter;

  const [apiKeys, setApiKeys] = useState([]);
  const [loadingKeys, setLoadingKeys] = useState(true);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyError, setApiKeyError] = useState(false);

  const { userId } = useParams();
  const [loading, setLoading] = useState(false);
  const [isSelection, setIsSelection] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [isAll, setIsAll] = useState(false);

  const handleSubmit = (type) => {
    setLoading(true);
    setIsSelection(type === 1);
    setIsAll(type === 0);
    setIsFilter(type === "filter");

    let mainFilterValues = [];
    let excluded = [];
    if (selectedColumns.length > 0) {
      selectedColumns.forEach((col) => {
        if (defaults.includes(col)) {
          let v = [
            {
              field_name: "status",
              type: "equals",
              value: col,
            },
            {
              field_name: "custom_status",
              type: "equals",
              value: "null",
            },
          ];

          if (query) {
            v.push({
              field_name: "any_datafield",
              type: "contains",
              value: query,
            });
          }

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        } else {
          let v = [
            {
              field_name: "custom_status",
              type: "equals",
              value: col.toString(),
            },
          ];

          if (filter) {
            v.push(filter);
          }

          mainFilterValues.push({
            mode: "and",
            values: v,
          });
        }
        if (unselectedIds[col]) {
          excluded.push(...unselectedIds[col]);
        }
      });
    }

    let parsedFilter = {
      mode: "or",
      values: mainFilterValues,
    };

    let justFilter = {
      mode: "and",
      values: [],
    };
    if (type === "filter" && filter) {
      justFilter.values.push(filter);
    }
    if (type === "filter" && query) {
      justFilter.values.push({
        field_name: "any_datafield",
        type: "contains",
        value: query,
      });
    }

    enrichPhonePrm({
      user_id: parseInt(userId),
      filter: type === "filter" ? justFilter : parsedFilter,
      contact_ids: Object.values(selectedIds).flatMap((x) => x),
      excluded_contact_ids: excluded,
      external_api_key_id: parseInt(apiKey),
      enrich_external_api: enrichType,
    })
      .then((response) => {
        handleClose?.();
        setLoadingJobs(true);
        handleFinish?.()
        if (isSelection) {
          deselectAll();
        }
      })
      .finally(() => {
        setLoading(false);
        setIsAll(false);
        setIsSelection(false);
        setIsFilter(false);
      });
  };

  const canExport = () => {
    if (Object.values(selectedIds).flatMap((x) => x).length > 0) {
      return true;
    }
    if (selectedColumns.length > 0) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    getAIKeys()
      .then((res) => {
        setApiKeys(
          res.data.external_api_keys_list?.filter((x) => x.type === enrichType)
        );
      })
      .finally(() => setLoadingKeys(false));
  }, []);

  return (
    <Popover zIndex={300} position="right-start">
      <Popover.Target>
        <Button
          variant="light"
          justify="start"
          leftSection={<IconUserSearch size={16} />}
          rightSection={<IconChevronRight size={16} />}
          styles={{
            label: {
              marginRight: "auto",
            },
          }}
        >
          Enrichir avec Dropcontact
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap={"xs"} w={250}>

          <Select
            id="api_key"
            label={"Clé API Dropcontact à utiliser"}
            required
            placeholder={"Clé API à utiliser"}
            flex={1}
            data={apiKeys.map((item) => ({
              label: item.name,
              value: item.id.toString(),
            }))}
            rightSection={loadingKeys ? <Loader size={16} /> : null}
            value={apiKey}
            onChange={setApiKey}
            error={
              apiKeyError
                ? lang.contact_lists.datafield_content.dialog.api_key_needed
                : null
            }
            comboboxProps={{ withinPortal: false }}
          />

          <Button
            loading={isSelection}
            disabled={!apiKey || !canExport() || loading}
            onClick={() => handleSubmit(1)}
          >
            {lang.contact_lists.export_contacts.selection}
          </Button>

          <Button
            loading={isFilter}
            disabled={
              !apiKey ||
              loading ||
              ((!filter || !filter?.values?.length === 0) && !query)
            }
            onClick={() => handleSubmit("filter")}
          >
            {lang.contact_lists.export_contacts.filter}
          </Button>

          <Button
            loading={isAll}
            disabled={!apiKey || loading}
            onClick={() => handleSubmit(0)}
          >
            {lang.contact_lists.enrich_contacts.enrich_all}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default LeadsEnrichEmail;
